import { ErrorText, FlexDiv, GenericDiv, GenericP, GenericSpan } from 'commonStyles';
import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
// import Markdown from 'react-markdown';
// import remarkGfm from 'remark-gfm';
import MarkdownJsx from 'markdown-to-jsx';

import {
	MessageContainer,
	GreetingText,
	SubtitleText,
	SuggestionCard,
	SuggestionIcon,
	ChatInput,
	SendButton,
	ActionButton,
	SuggestionPill,
	MessageBubble,
	WelcomeContainerScroll
} from './ChatInterfaceStyles';
import { getChatHistory, getChatResponseStream } from '../landingApi';
import Loader from 'common/Loader/Loader';
import { getConsulData } from 'UtilityComponents/index';

const consulData = getConsulData('config').CHAT_BOT || {};

// Myra icon component for suggestion cards
const MyraIconSvg = () => (
	<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
		<text x='2' y='18' fontSize='16' fontStyle='italic' fill='#F95776'>my</text>
		<circle cx='20' cy='6' r='2' fill='#F95776' />
	</svg>
);

// Send icon component
const SendIcon = () => (
	<svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
		<path d='M12 5V19M12 5L6 11M12 5L18 11' stroke='#F95776' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
	</svg>
);

export default function ChatInterface({ userName }) {
	const containerref = useRef(null);
	const [messages, setMessages] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isActionAvailable, setIsActionAvailable] = useState(false);
	const [streamStatus, setStreamStatus] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [inputValue, setInputValue] = useState('');
	const [sampleMessages, setSampleMessages] = useState([]);

	const displayName = userName || 'there';

	const changeInputFld = (e) => {
		setInputValue(e.target.value);
	};

	const sendMessage = async (type, query, approved) => {
		setErrorMessage('');
		setMessages((prev) => [...prev, { text: query, sender: 'user', type: type }]);

		const streamCallback = (streamChunk) => {
			streamChunk.type == 'status' && streamChunk.message && setStreamStatus(streamChunk.message);
			if (streamChunk.type === 'result') {
				const response = streamChunk.data;
				let message = response.data.message;
				try {
					message = message.replace(/^---|---$/g, '');
				} catch (e) {
					// console.error('Error parsing message:', e);
				}
				let approveBtn = response.data.approve_btn;
				let rejectBtn = response.data.reject_btn;
				if (response.follow_up_questions?.length > 0) {
					const followUpQuestion = response.follow_up_questions?.map((item) => ({
						header: item,
						text: item
					}));
					setSampleMessages(followUpQuestion);
				} else {
					// setSampleMessages(consulData.SUGGESTIONS);
				}
				setMessages((prev) => [...prev, { text: message, sender: 'bot', score: response.data.score, type: type, actions: { approve: approveBtn, reject: rejectBtn } }]);
				setIsActionAvailable(approveBtn || rejectBtn);
				setIsLoading(false);
				setStreamStatus('');
				if (containerref.current) {
					containerref.current.scrollTo({
						top: containerref.current.scrollHeight,
						behavior: 'smooth'
					});
				}
			}
			if (streamChunk.type === 'error') {
				setErrorMessage(streamChunk.message || 'Something went wrong. Please try again later.');
			}
			if (streamChunk.type === 'stream_complete') {
				setStreamStatus('');
				setIsLoading(false);
			}
		};
		try {
			setInputValue('');
			setIsLoading(true);
			setStreamStatus('Finding perfect result for your query...');
			await getChatResponseStream({ query, approved }, streamCallback);
		} catch (e) {
			setStreamStatus('');
			setIsLoading(false);
			setErrorMessage('Something went wrong. Please try again later.');
			setTimeout(() => {
				setErrorMessage('');
			}, 5000);
			console.error('Error sending message:', e);
		}
	};

	const sendMessageHandler = async (type, query, approved) => {
		switch (type) {
			case 'SAMPLE':
				sendMessage('sample', query);
				break;
			case 'CHAT':
				if (inputValue.trim()) {
					sendMessage('chat', inputValue);
				}
				break;
			case 'ACTION':
				sendMessage('action', query, approved);
				break;
			default:
				sendMessage('', query);
		}
	};

	const handleSampleMessage = (text) => {
		sendMessageHandler('SAMPLE', text);
	};

	useEffect(() => {
		const fetchChatHistory = async () => {
			setIsLoading(true);
			const { messages = [], isActionAvailable } = await getChatHistory() || [];
			setIsLoading(false);
			setMessages(messages);
			setIsActionAvailable(isActionAvailable);
		};
		fetchChatHistory();
	}, []);

	useLayoutEffect(() => {
		if (containerref.current) {
			containerref.current.scrollTo({
				top: containerref.current.scrollHeight,
				behavior: 'smooth'
			});
		}
	}, [messages?.length, streamStatus]);

	const handleAction = (action, btnText) => {
		sendMessageHandler('ACTION', btnText, action === 'approve');
	};

	const messageLength = messages?.length || 0;
	const hasMessages = messageLength > 0;

	// Welcome screen when no messages
	if (!hasMessages && !isLoading) {
		return (
			<FlexDiv $width='100%' $direction='column' height='100%' justify='space-between' $bg='#ffffff'>
				<FlexDiv height='100%' padding='24px' $direction='column' justify='flex-start'>
					<GreetingText as='h2'>Hi, {displayName}</GreetingText>
					<SubtitleText as='h3' font_size='15'>
						I'm Myra — your personal account Manager.<br />
						Let's manage your account together.
					</SubtitleText>

					<GenericP fsize='12px' color='#000' f_weight='500' bottom_M='5'>You may try asking</GenericP>
					<WelcomeContainerScroll top_P='16' bottom_P='16' $direction='column' justify='flex-start' align='flex-start' max_height='80px' className='custom-scroll'>
						{consulData.SUGGESTIONS?.map((msg, index) => (
							<SuggestionCard
								key={index}
								as='button'
								$direction='row'
								align='center'
								justify='flex-start'
								padding='10px 12px'
								bottom_M='10'
								bdr='1px solid #e5e7eb'
								bdrRad='12px'
								cursor='pointer'
								opacity={isActionAvailable || isLoading ? '0.5' : '1'}
								transition='all 0.2s ease'
								text_align='left'
								onClick={() => handleSampleMessage(msg.text)}>
								<SuggestionIcon
									$width='32px'
									height='32px'
									$bg='rgb(253, 248, 249)'
									bdrRad='10px'
									display='flex'
									align='center'
									justify='center'
									right_M='14'>
									<MyraIconSvg />
								</SuggestionIcon>
								<GenericSpan fsize='15px' color='#1a1a1a' f_weight='500'>{msg.header}</GenericSpan>
							</SuggestionCard>
						))}
					</WelcomeContainerScroll>
				</FlexDiv>

				<GenericDiv padding='16px 16px 0' $bg='#ffffff'>
					<FlexDiv
						$direction='row'
						align='center'
						padding='30px 16px'
						bdrRad='12px 12px 0 30px'
						boxShadow='rgba(53, 95, 242, 0.14) 0px 0px 20px'>
						<ChatInput
							type='text'
							placeholder='Ask me anything'
							value={inputValue}
							onChange={changeInputFld}
							onKeyDown={(e) => e.key === 'Enter' && sendMessageHandler('CHAT')}
							disabled={isActionAvailable}
						/>
						<SendButton
							disabled={isLoading || isActionAvailable || !inputValue.trim()}
							onClick={() => sendMessageHandler('CHAT')}>
							<SendIcon />
						</SendButton>
					</FlexDiv>
				</GenericDiv>
			</FlexDiv>
		);
	}

	const renderMessage = (text) => {
		return <MarkdownJsx>{text}</MarkdownJsx>;
	};

	// Chat view when there are messages
	return (
		<FlexDiv $width='100%' $direction='column' height='100%' justify='flex-end' $bg='#ffffff'>
			<MessageContainer ref={containerref}>
				<FlexDiv justify='flex-end' $direction='column' padding='8px'>
					{messages?.map((msg, index) => {
						const isLastMessage = index === messageLength - 1;
						const isApproveBtn = msg.actions?.approve;
						const isRejectBtn = msg.actions?.reject;
						return msg.text ? (
							<MessageBubble key={index} $isUser={msg.sender === 'user'}>
								{renderMessage(msg.text)}

								{isActionAvailable && isLastMessage && (
									<FlexDiv justify='flex-start' top_M='15' bottom_P='15'>
										{isApproveBtn && (
											<ActionButton onClick={() => handleAction('approve', isApproveBtn)}>
												{isApproveBtn}
											</ActionButton>
										)}
										{isRejectBtn && (
											<ActionButton $variant='secondary' onClick={() => handleAction('reject', isRejectBtn)}>
												{isRejectBtn}
											</ActionButton>
										)}
									</FlexDiv>
								)}
							</MessageBubble>
						) : null;
					})}
					{streamStatus && <GenericSpan fsize='12px' color='#6b7280' f_weight='500'>{streamStatus}</GenericSpan>}
					{errorMessage && <ErrorText fsize='12px' color='#ef4444' f_weight='500'>{errorMessage}</ErrorText>}
				</FlexDiv>
			</MessageContainer>

			{sampleMessages?.length > 0 && (
				<FlexDiv
					overflow='auto'
					max_height='80px'
					className='custom-scroll'
					$width='100%'
					justify='flex-start'
					wrap='wrap'
					padding='24px 16px'
					style={{ borderTop: '1px solid #f0f0f0' }}>
					{sampleMessages.map((msg, index) => (
						<SuggestionPill
							key={index}
							disabled={isActionAvailable && isLoading}
							onClick={() => handleSampleMessage(msg.text)}>
							{msg.header}
						</SuggestionPill>
					))}
				</FlexDiv>
			)}

			<FlexDiv
				$width='100%'
				padding='0 16px'>
				<FlexDiv
					$direction='row'
					align='center'
					padding='24px 16px'
					bdrRad='12px 12px 0 30px'
					boxShadow='rgba(53, 95, 242, 0.14) 0px 0px 20px'>
					<ChatInput
						type='text'
						placeholder='Ask me anything'
						value={inputValue}
						onChange={changeInputFld}
						onKeyDown={(e) => e.key === 'Enter' && sendMessageHandler('CHAT')}
						disabled={isActionAvailable}
					/>
					<SendButton
						disabled={isLoading || isActionAvailable || !inputValue.trim()}
						onClick={() => sendMessageHandler('CHAT')}>
						{isLoading ? <Loader margin='0' color='#3b82f6' size='6' /> : <svg width='24' height='24' viewBox='0 0 24 24' fill='none'><path fillRule='evenodd' clipRule='evenodd' d='M20.247 10.52l-1.706 5.15c-1.204 3.63-1.805 5.446-2.688 5.962-.839.49-1.876.49-2.715 0-.882-.516-1.484-2.331-2.687-5.962-.193-.583-.29-.874-.452-1.118a2.167 2.167 0 00-.594-.597c-.242-.163-.532-.26-1.111-.454-3.61-1.21-5.415-1.816-5.928-2.703a2.73 2.73 0 010-2.731c.513-.887 2.318-1.492 5.928-2.703l5.12-1.716c4.472-1.5 6.709-2.25 7.89-1.062 1.18 1.187.434 3.436-1.057 7.935zm-7.264.382a.753.753 0 01.005-1.06l4.187-4.165a.743.743 0 011.055.006c.29.295.286.77-.006 1.06l-4.187 4.165a.743.743 0 01-1.054-.006z' fill='url(#paint0_linear_2242_25334)' /><path fillRule='evenodd' clipRule='evenodd' d='M20.247 10.52l-1.706 5.15c-1.204 3.63-1.805 5.446-2.688 5.962-.839.49-1.876.49-2.715 0-.882-.516-1.484-2.331-2.687-5.962-.193-.583-.29-.874-.452-1.118a2.167 2.167 0 00-.594-.597c-.242-.163-.532-.26-1.111-.454-3.61-1.21-5.415-1.816-5.928-2.703a2.73 2.73 0 010-2.731c.513-.887 2.318-1.492 5.928-2.703l5.12-1.716c4.472-1.5 6.709-2.25 7.89-1.062 1.18 1.187.434 3.436-1.057 7.935zm-7.264.382a.753.753 0 01.005-1.06l4.187-4.165a.743.743 0 011.055.006c.29.295.286.77-.006 1.06l-4.187 4.165a.743.743 0 01-1.054-.006z' fill='url(#paint1_linear_2242_25334)' /><defs><linearGradient id='paint0_linear_2242_25334' x1='15.8624' y1='18.2791' x2='7.62381' y2='17.9217' gradientUnits='userSpaceOnUse'><stop stopColor='#2493EB' /><stop offset='1' stopColor='#50F' /></linearGradient><linearGradient id='paint1_linear_2242_25334' x1='8.02336' y1='6.4186' x2='15.9776' y2='18.2791' gradientUnits='userSpaceOnUse'><stop stopColor='#51AFE6' /><stop offset='0.576933' stopColor='#355FF2' /><stop offset='1' stopColor='#11287A' /></linearGradient></defs></svg>}
					</SendButton>
				</FlexDiv>
			</FlexDiv>
		</FlexDiv>
	);
}
